// packages/core/src/sync/HttpSyncClient.ts
// ─────────────────────────────────────────────────────────────────────────
// The network half of the sync engine. Talks to the Nisk .NET API. View-layer
// free and platform-agnostic: it takes a token getter (so web uses Firebase
// getIdToken and mobile can supply its own) and a base URL. The SyncEngine
// owns the queue/storage; this owns the wire.
//
// Field names here mirror the server's WireContracts.cs 1:1 (camelCase). The
// server derives userId from the JWT, so userId is NEVER sent in a request body.
//
// Economy operations DO NOT go through /operations — the server rejects them in
// the operation log. They go through applyEconomy(). The SyncEngine does the
// fan-out; this client just exposes both endpoints.
//
// PLATFORM PURITY: core's tsconfig intentionally omits the DOM lib so this
// package can target React Native too. `fetch` is now a cross-platform standard
// (Node 18+, modern RN, browsers), but its TYPE isn't in core's lib. Rather
// than pull DOM into core, we declare the minimal Fetch surface we use and read
// the implementation off globalThis. Hosts that lack a global fetch must pass
// `fetchImpl` explicitly.
// ─────────────────────────────────────────────────────────────────────────

import type { OperationType } from './SyncTypes';

// ── Minimal cross-platform fetch typing ─────────────────────────────────────
// Just the slice of the Fetch API this client touches. Avoids depending on the
// DOM lib's `fetch`/`Response`/`RequestInit` globals.

export interface FetchResponseLike {
	readonly ok: boolean;
	readonly status: number;
	json(): Promise<unknown>;
	text(): Promise<string>;
}

export interface FetchInitLike {
	method?: string;
	headers?: Record<string, string>;
	body?: string;
}

export type FetchLike = (
	url: string,
	init?: FetchInitLike,
) => Promise<FetchResponseLike>;

/** Reads a global fetch off the host without requiring the DOM lib. */
function globalFetch(): FetchLike {
	const f = (globalThis as { fetch?: unknown }).fetch;
	if (typeof f !== 'function') {
		throw new Error(
			'No global fetch available; pass fetchImpl to HttpSyncClient.',
		);
	}
	return (f as (...a: unknown[]) => unknown).bind(globalThis) as FetchLike;
}

// ── Token + config ─────────────────────────────────────────────────────────

/**
 * Returns a Firebase ID token. `forceRefresh` is passed straight to Firebase's
 * getIdToken(forceRefresh) so the client can recover from a 401 by minting a
 * fresh token and retrying once.
 */
export type TokenGetter = (forceRefresh: boolean) => Promise<string | null>;

export interface HttpSyncClientConfig {
	/** API origin, e.g. "https://api.nisk.app" or "http://localhost:5080". */
	baseUrl: string;
	/** Supplies a bearer token; web wires this to Firebase auth.currentUser. */
	getToken: TokenGetter;
	/** Override for tests / hosts without a global fetch. */
	fetchImpl?: FetchLike;
}

// ── Wire shapes (match WireContracts.cs) ───────────────────────────────────

/** Push body item. No userId — the server reads it from the JWT. */
export interface WireOperation {
	id: string;
	deviceId: string;
	type: OperationType;
	entityId: string;
	idempotencyKey: string;
	payload: unknown;
	clientTimestamp: number;
}

export interface PushResponseWire {
	attempted: number;
	synced: number;
	rejected: number;
	rejections: Array<{ operationId: string; reason: string }>;
}

export interface PulledOperationWire {
	id: string;
	deviceId: string;
	type: OperationType;
	entityId: string;
	idempotencyKey: string;
	payload: unknown;
	clientTimestamp: number;
	serverTimestamp: number;
}

export interface PullResponseWire {
	operations: PulledOperationWire[];
	/** Pass back as ?since= to continue paging; null means caught up. */
	nextSince: number | null;
}

export type EconomyKindWire = 'earn' | 'spend' | 'iap';
export type CurrencyWire = 'xp' | 'gems';

export interface EconomyRequestWire {
	idempotencyKey: string;
	kind: EconomyKindWire;
	currency: CurrencyWire;
	amount: number; // positive magnitude
	iapReceipt?: unknown;
}

export type EconomyStatusWire =
	| 'applied'
	| 'duplicate'
	| 'insufficient_funds'
	| 'invalid_amount';

export interface EconomyResponseWire {
	status: EconomyStatusWire;
	balanceAfter: number;
}

export interface BalanceResponseWire {
	xp: number;
	gems: number;
}

export type AcceptanceDocTypeWire = 'tos' | 'privacy' | 'disclaimer' | 'age';

export interface AcceptanceRequestWire {
	docType: AcceptanceDocTypeWire;
	version: string;
}

/** One accepted doc + the latest version/time, from GET /v1/acceptance/status. */
export interface AcceptanceStatusItemWire {
	docType: AcceptanceDocTypeWire;
	version: string;
	acceptedAt: string; // ISO
}

/** Server-authoritative acceptance state for the signed-in user. */
export interface AcceptanceStatusResponseWire {
	accepted: AcceptanceStatusItemWire[];
}

/**
 * Legacy economy snapshot carried by migration. The old client stored XP/gems
 * as accumulated balances, never a replayable earn-stream, so they're seeded
 * once as an authoritative opening balance server-side (see the API's
 * IEconomyStore.SeedOpeningBalanceAsync). Omit when there's nothing to seed;
 * either field may be 0. Positive magnitudes.
 */
export interface EconomyOpeningWire {
	xp: number;
	gems: number;
}

export interface MigrateRequestWire {
	deviceId: string;
	legacyKeysFound: string[];
	operations: WireOperation[];
	opening?: EconomyOpeningWire;
}

export interface MigrateResponseWire {
	alreadyMigrated: boolean;
	replayed: number;
	/** Opening XP actually written (0 if skipped because a balance already existed). */
	seededXp: number;
	/** Opening gems actually written (0 if skipped). */
	seededGems: number;
}

/**
 * Returned by DELETE /v1/account ONLY when the server deleted the DB data but
 * could not remove the Firebase Auth identity (e.g. Admin deletion not
 * configured, or a transient failure), so the caller knows to remove the
 * identity client-side. On full success the endpoint returns 204 (no body).
 */
export interface AccountDeleteResponseWire {
	dataDeleted: boolean;
	authUserDeleted: boolean;
}

// ── Errors ─────────────────────────────────────────────────────────────────

export class SyncHttpError extends Error {
	constructor(
		public readonly status: number,
		public readonly bodyText: string,
		public readonly url: string,
	) {
		super(`Nisk API ${status} on ${url}: ${bodyText.slice(0, 200)}`);
		this.name = 'SyncHttpError';
	}
}

/** Thrown when no token is available — caller is unauthenticated. */
export class NotAuthenticatedError extends Error {
	constructor() {
		super('No Firebase ID token available; user is not signed in.');
		this.name = 'NotAuthenticatedError';
	}
}

// ── Client ───────────────────────────────────────────────────────────────

export class HttpSyncClient {
	private readonly baseUrl: string;
	private readonly getToken: TokenGetter;
	private readonly fetchImpl: FetchLike;

	constructor(config: HttpSyncClientConfig) {
		// Normalize: drop a trailing slash so path joins are predictable.
		this.baseUrl = config.baseUrl.replace(/\/+$/, '');
		this.getToken = config.getToken;
		this.fetchImpl = config.fetchImpl ?? globalFetch();
	}

	// ── Endpoints ────────────────────────────────────────────────────────

	/** POST /v1/operations — push NON-economy operations only. */
	async pushOperations(
		operations: WireOperation[],
	): Promise<PushResponseWire> {
		return this.request<PushResponseWire>('POST', '/v1/operations', {
			operations,
		});
	}

	/** GET /v1/operations?since= — pull one page of other-device operations. */
	async pullOperations(since: number): Promise<PullResponseWire> {
		const qs = `?since=${encodeURIComponent(String(since))}`;
		return this.request<PullResponseWire>('GET', `/v1/operations${qs}`);
	}

	/** POST /v1/economy — apply one earn/spend/iap. Transactional server-side. */
	async applyEconomy(req: EconomyRequestWire): Promise<EconomyResponseWire> {
		// 409 (insufficient funds) and 400 (invalid amount) carry a typed body;
		// surface them as values, not thrown errors, so the engine can record
		// the rejection rather than abort the whole push.
		return this.requestAllowing<EconomyResponseWire>(
			'POST',
			'/v1/economy',
			[400, 409],
			req,
		);
	}

	/** GET /v1/economy/balance — server-authoritative balances. */
	async getBalance(): Promise<BalanceResponseWire> {
		return this.request<BalanceResponseWire>('GET', '/v1/economy/balance');
	}

	/** POST /v1/acceptance — record a legal acceptance. */
	async recordAcceptance(req: AcceptanceRequestWire): Promise<void> {
		await this.request<void>('POST', '/v1/acceptance', req, true);
	}

	/** GET /v1/acceptance/status — server-authoritative acceptance state. */
	async getAcceptanceStatus(): Promise<AcceptanceStatusResponseWire> {
		return this.request<AcceptanceStatusResponseWire>(
			'GET',
			'/v1/acceptance/status',
		);
	}

	/** POST /v1/migrate — one-time legacy replay for this device. */
	async migrate(req: MigrateRequestWire): Promise<MigrateResponseWire> {
		return this.request<MigrateResponseWire>('POST', '/v1/migrate', req);
	}

	/**
	 * DELETE /v1/account — purge server data and (when configured) remove the
	 * Firebase Auth identity via the Admin SDK. Server retains acceptance records
	 * (legal hold). Returns null on full success (204), or the response body when
	 * the server deleted the data but could NOT remove the auth user
	 * (authUserDeleted=false) — the caller should then remove the identity
	 * client-side as a fallback.
	 *
	 * MUST be called while the Firebase token is still valid (i.e. BEFORE any
	 * client-side deleteUser), or the request will 401.
	 */
	async deleteAccount(): Promise<AccountDeleteResponseWire | null> {
		// noContent=false so a 200 body is parsed; the transport still returns
		// undefined on a 204 (full success), which we normalize to null.
		const body = await this.request<AccountDeleteResponseWire | undefined>(
			'DELETE',
			'/v1/account',
			undefined,
			false,
		);
		return body ?? null;
	}

	// ── Transport ────────────────────────────────────────────────────────

	private async request<T>(
		method: string,
		path: string,
		body?: unknown,
		noContent = false,
	): Promise<T> {
		return this.requestAllowing<T>(method, path, [], body, noContent);
	}

	/**
	 * Core transport. Attaches the bearer token, sends JSON, and on a 401 mints
	 * a fresh token (forceRefresh) and retries exactly once. Status codes in
	 * `allowedErrorStatuses` are returned as parsed bodies instead of thrown,
	 * so callers can treat them as typed outcomes.
	 */
	private async requestAllowing<T>(
		method: string,
		path: string,
		allowedErrorStatuses: number[],
		body?: unknown,
		noContent = false,
	): Promise<T> {
		const url = `${this.baseUrl}${path}`;

		const send = async (
			forceRefresh: boolean,
		): Promise<FetchResponseLike> => {
			const token = await this.getToken(forceRefresh);
			if (!token) throw new NotAuthenticatedError();

			const headers: Record<string, string> = {
				Authorization: `Bearer ${token}`,
			};
			if (body !== undefined)
				headers['Content-Type'] = 'application/json';

			return this.fetchImpl(url, {
				method,
				headers,
				body: body !== undefined ? JSON.stringify(body) : undefined,
			});
		};

		let res = await send(false);
		if (res.status === 401) {
			// Token may have expired; force a refresh and retry once.
			res = await send(true);
		}

		if (res.ok) {
			if (noContent || res.status === 204) return undefined as T;
			return (await res.json()) as T;
		}

		if (allowedErrorStatuses.includes(res.status)) {
			// Typed error body (e.g. insufficient_funds) — return, don't throw.
			return (await res.json()) as T;
		}

		const text = await res.text().catch(() => '');
		throw new SyncHttpError(res.status, text, url);
	}
}
